import React, { useCallback, useEffect, useState } from 'react';
import CountryFlag, { countryFromRegion } from '../../../shared/components/CountryFlag';
import {
    Card, Table, Tag, Space, Button, Segmented, Modal, Form, Input, InputNumber, Select, Switch, Tooltip, Typography,
    Descriptions, Popconfirm, List, Empty, message,
} from 'antd';
import { ReloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ApiOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import cdnProbeService from '../../services/cdnProbeService';

const { Text } = Typography;
const fmtTime = (s) => (s ? new Date(s.replace(' ', 'T')).toLocaleTimeString() : '-');
const ms = (v) => (v === null || v === undefined ? '-' : `${v} ms`);
const pct = (v) => (v === null || v === undefined ? '-' : `${(v * 100).toFixed(v < 0.1 && v > 0 ? 1 : 0)}%`);

// 颜色:失败率优先,其次延迟
const cellColor = (cell, metric) => {
    if (!cell || !cell.samples) return '#f0f0f0';
    if (cell.fail_rate > 0.2) return '#ff4d4f';
    if (metric === 'fail_rate') return cell.fail_rate > 0.02 ? '#faad14' : '#52c41a';
    const v = cell[metric];
    if (v === null || v === undefined) return '#ff4d4f';
    const [ok, warn] = metric.startsWith('connect') ? [100, 250] : [400, 1000];
    if (cell.fail_rate > 0.02 || v > warn) return v > warn ? '#ff7875' : '#faad14';
    return v > ok ? '#ffd666' : '#95de64';
};

const cellText = (cell, metric) => {
    if (!cell || !cell.samples) return '-';
    return metric === 'fail_rate' ? pct(cell.fail_rate) : (cell[metric] ?? '×');
};

/** 回源质量:CDN 节点 × 源站 的主动探测矩阵(p50/p95/失败率),可下钻 */
const OriginQuality = () => {
    const { t } = useTranslation();
    const [win, setWin] = useState(300);
    const [metric, setMetric] = useState('ttfb_p95');
    const [data, setData] = useState({ targets: [], nodes: [], cells: {} });
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    const [editing, setEditing] = useState(null);
    const [search, setSearch] = useState({ q: '', items: [], loading: false });
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        const r = await cdnProbeService.getOriginMatrix(win);
        setLoading(false);
        if (r.success) setData(r.data || { targets: [], nodes: [], cells: {} });
        else message.error(r.error || t('Failed to load'));
    }, [win, t]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, [load]);

    const openDetail = async (target, node) => {
        setDetail({ target, node, loading: true });
        const r = await cdnProbeService.getOriginDetail(target.id, node.id);
        setDetail({ target, node, loading: false, data: r.success ? r.data : null });
    };

    const openEdit = (target) => {
        setEditing(target || {});
        setSearch({ q: '', items: [], loading: false });
        form.setFieldsValue(target || { scheme: 'https', port: 443, path: '/', verify_tls: false, enabled: true });
    };

    const saveTarget = async () => {
        const v = await form.validateFields();
        const r = await cdnProbeService.saveOriginTarget({ ...v, id: editing?.id });
        if (r.success) { setEditing(null); load(); } else message.error(r.error || t('Save failed'));
    };

    const removeTarget = async (id) => {
        const r = await cdnProbeService.deleteOriginTarget(id);
        if (r.success) load(); else message.error(r.error || t('Delete failed'));
    };

    const runSearch = async (q) => {
        setSearch((s) => ({ ...s, q, loading: true }));
        const r = await cdnProbeService.searchDomainOrigins(q);
        setSearch({ q, items: r.success ? r.data?.items || [] : [], loading: false });
    };

    const columns = [
        {
            title: t('Origin'), dataIndex: 'name', fixed: 'left', width: 230,
            render: (n, tg) => (
                <Space direction="vertical" size={0}>
                    <Space size={4}>
                        <strong>{n}</strong>
                        {!tg.enabled && <Tag>{t('Disabled')}</Tag>}
                        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(tg)} />
                        <Popconfirm title={t('Delete?')} onConfirm={() => removeTarget(tg.id)}>
                            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {tg.scheme}://{tg.address}:{tg.port}{tg.scheme !== 'tcp' ? tg.path : ''}{tg.host_header ? ` · Host ${tg.host_header}` : ''}
                    </Text>
                </Space>
            ),
        },
        ...data.nodes.map((n) => ({
            title: (
                <Tooltip title={`${n.name}${n.region ? ` · ${n.region}` : ''}`}>
                    <Space direction="vertical" size={0} style={{ fontSize: 12 }}>
                        <span><CountryFlag code={countryFromRegion(n.region)} />{n.name}</span>
                        <Tag style={{ marginInlineEnd: 0 }}>{n.tier}</Tag>
                    </Space>
                </Tooltip>
            ),
            key: `n${n.id}`, align: 'center', width: 96,
            render: (_, tg) => {
                const cell = data.cells[`${tg.id}:${n.id}`];
                return (
                    <Tooltip title={cell?.samples ? `${t('Samples')} ${cell.samples} · ${t('Failure rate')} ${pct(cell.fail_rate)} · TTFB p50/p95 ${ms(cell.ttfb_p50)} / ${ms(cell.ttfb_p95)}` : t('No data')}>
                        <div
                            onClick={() => cell?.samples && openDetail(tg, n)}
                            style={{ background: cellColor(cell, metric), borderRadius: 4, padding: '6px 2px', cursor: cell?.samples ? 'pointer' : 'default', fontSize: 12 }}
                        >
                            {cellText(cell, metric)}
                        </div>
                    </Tooltip>
                );
            },
        })),
    ];

    const d = detail?.data;
    return (
        <Card
            title={<Space><ApiOutlined />{t('Origin quality')}</Space>}
            extra={
                <Space wrap>
                    <Segmented size="small" value={win} onChange={setWin} options={[{ label: '5m', value: 300 }, { label: '1h', value: 3600 }]} />
                    <Select size="small" value={metric} onChange={setMetric} style={{ width: 150 }}
                        options={[
                            { value: 'ttfb_p95', label: 'TTFB p95' }, { value: 'ttfb_p50', label: 'TTFB p50' },
                            { value: 'connect_p95', label: `${t('Connect')} p95` }, { value: 'fail_rate', label: t('Failure rate') },
                        ]} />
                    <Button size="small" icon={<PlusOutlined />} onClick={() => openEdit(null)}>{t('Add target')}</Button>
                    <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} />
                </Space>
            }
        >
            <Table size="small" rowKey="id" columns={columns} dataSource={data.targets} loading={loading}
                pagination={false} scroll={{ x: 'max-content' }} locale={{ emptyText: <Empty /> }} />

            <Modal open={!!detail} onCancel={() => setDetail(null)} footer={null} width={860} destroyOnClose
                title={detail ? `${detail.target.name} · ${detail.node.name}` : ''}>
                {detail?.loading && <Text type="secondary">…</Text>}
                {d && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Descriptions size="small" bordered column={2}>
                            {[['5m', d.m5], ['1h', d.h1]].map(([k, s]) => (
                                <Descriptions.Item key={k} label={k}>
                                    {s.samples} · {t('Failure rate')} {pct(s.fail_rate)} · TTFB {ms(s.ttfb_p50)} / {ms(s.ttfb_p95)}
                                </Descriptions.Item>
                            ))}
                        </Descriptions>
                        <Table size="small" pagination={false} rowKey="k"
                            dataSource={['dns_ms', 'connect_ms', 'tls_ms', 'ttfb_ms', 'total_ms'].map((k) => ({ k, ...d.stages[k] }))}
                            columns={[
                                { title: '1h', dataIndex: 'k', render: (k) => ({ dns_ms: 'DNS', connect_ms: t('Connect'), tls_ms: 'TLS', ttfb_ms: 'TTFB', total_ms: t('Total') }[k]) },
                                { title: 'p50', dataIndex: 'p50', render: ms },
                                { title: 'p95', dataIndex: 'p95', render: ms },
                            ]} />
                        {Object.keys(d.errors || {}).length > 0 && (
                            <Space wrap>{Object.entries(d.errors).map(([e, n]) => <Tag color="error" key={e}>{e} × {n}</Tag>)}</Space>
                        )}
                        <Table size="small" rowKey={(r) => r.at + r.total} dataSource={d.recent} pagination={{ pageSize: 10 }}
                            columns={[
                                { title: t('Time'), dataIndex: 'at', render: fmtTime },
                                { title: '', dataIndex: 'ok', render: (ok) => (ok ? <Tag color="success">OK</Tag> : <Tag color="error">×</Tag>) },
                                { title: 'DNS', dataIndex: 'dns', render: ms },
                                { title: t('Connect'), dataIndex: 'connect', render: ms },
                                { title: 'TLS', dataIndex: 'tls', render: ms },
                                { title: 'TTFB', dataIndex: 'ttfb', render: ms },
                                { title: t('Total'), dataIndex: 'total', render: ms },
                                { title: 'HTTP', dataIndex: 'code', render: (c, r) => r.err || c || '-' },
                            ]} />
                    </Space>
                )}
            </Modal>

            <Modal open={!!editing} title={editing?.id ? editing.name : t('Add target')} onCancel={() => setEditing(null)} onOk={saveTarget} width={620} destroyOnClose>
                {!editing?.id && (
                    <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }}>
                        <Input.Search placeholder={t('Add from a CDN domain')} enterButton={<SearchOutlined />} loading={search.loading} onSearch={runSearch} allowClear />
                        {search.items.length > 0 && (
                            <List size="small" bordered style={{ maxHeight: 200, overflow: 'auto' }}
                                dataSource={search.items.flatMap((dm) => dm.origins.map((o) => ({ ...o, domain_id: dm.domain_id })))}
                                renderItem={(o) => (
                                    <List.Item style={{ cursor: 'pointer' }} onClick={() => form.setFieldsValue({ ...o, path: '/', verify_tls: false, enabled: true })}>
                                        <Text>{o.name}</Text>
                                        <Text type="secondary">{o.scheme}:{o.port}</Text>
                                    </List.Item>
                                )} />
                        )}
                    </Space>
                )}
                <Form form={form} layout="vertical">
                    <Form.Item name="domain_id" hidden><Input /></Form.Item>
                    <Form.Item name="name" label={t('Name')}><Input maxLength={100} /></Form.Item>
                    <Space wrap>
                        <Form.Item name="scheme" label={t('Protocol')}>
                            <Select style={{ width: 100 }} options={['https', 'http', 'tcp'].map((v) => ({ value: v, label: v }))} />
                        </Form.Item>
                        <Form.Item name="address" label={t('Address')} rules={[{ required: true }]}><Input style={{ width: 220 }} placeholder="1.2.3.4 / origin.example.com" /></Form.Item>
                        <Form.Item name="port" label={t('Port')}><InputNumber min={1} max={65535} /></Form.Item>
                    </Space>
                    <Space wrap>
                        <Form.Item name="path" label={t('Path')}><Input style={{ width: 160 }} /></Form.Item>
                        <Form.Item name="host_header" label="Host"><Input style={{ width: 180 }} /></Form.Item>
                        <Form.Item name="sni" label="SNI"><Input style={{ width: 180 }} /></Form.Item>
                    </Space>
                    <Space size={24}>
                        <Form.Item name="verify_tls" label={t('Verify certificate')} valuePropName="checked"><Switch /></Form.Item>
                        <Form.Item name="enabled" label={t('Enabled')} valuePropName="checked"><Switch /></Form.Item>
                    </Space>
                </Form>
            </Modal>
        </Card>
    );
};

export default OriginQuality;
